import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import { useCurrentUser } from '@hooks/users';
import { isUserSubscribed } from '@helpers';

const DEFAULT_MESSAGE = 'Subscribe to continue your learning.';

interface RequireSubscribedOptions {
  message?: string;
}

/**
 * Wrap an action handler so students without an active subscription are
 * redirected to /subscribe (with a toast) instead of running the action.
 * Non-students pass through unchanged (isUserSubscribed returns true for them).
 */
export const useRequireSubscribed = () => {
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();

  return useCallback(
    <TArgs extends unknown[], TReturn>(
      action: (...args: TArgs) => TReturn,
      options?: RequireSubscribedOptions
    ) => {
      return (...args: TArgs): TReturn | void => {
        if (user && !isUserSubscribed(user)) {
          toast.error(options?.message ?? DEFAULT_MESSAGE);
          navigate({ to: '/subscribe' });
          return;
        }
        return action(...args);
      };
    },
    [user, navigate]
  );
};
